import { useRouter } from 'next/router'
import { ReactNode, createContext, useCallback, useMemo, useState } from 'react'
import { ICheckoutContext } from 'types/contexts'
import { InventoryItem, Venue, VenueEvent } from 'types/model'
import { Booking, BookingCreateRequest } from 'types/model/booking'

import { useOpenState } from '@hooks/open-state'
import { useSession } from '@hooks/session'
import useRequest from '@hooks/request'
import { useQueryParams } from '@hooks/query-params'
import { clearCart } from '@utils/cart'
import { getId, stripPhoneNumber } from '@utils/strings'
import { Api } from '@lib/api'
import { env } from '@lib/env'
import { buildCheckoutPath } from '@modules/widget/utils'
import { resolveCheckoutIdentity } from './checkoutIdentity'

import {
  bookingApi,
  useBooking,
  useBookingTickets,
} from '@modules/booking/data'
import { useInventory } from '@modules/inventory/data'
import { widgetEventManager } from '@modules/widget/events'
import { CheckoutFormValues } from 'types/model/checkout'
import { analytics } from '@lib/analytics'
import { TrackEvent } from '@utils/analytics/types'
import {
  getLuxonDateFormatByVenueDateDisplayFormat,
  utcToISO,
  withDateFormat,
} from '@utils/date-time'
import {
  floatingUtcWallClockToTrueUtcMs,
  formDateTimeToUTCISO,
  TrackingEvent,
} from 'shared'
import { trackEvent } from '@lib/track'
import { isPaymentLinkCheckout } from '@modules/booking/arrivalTimeField'

export const checkoutContext = createContext<ICheckoutContext>(
  {} as ICheckoutContext
)

type CheckoutIdentity = {
  authToken?: string | null
  prospectToken?: string | null
}

function normalizeArrivalTimeMs(
  value: number | null | undefined
): number | null | undefined {
  if (value == null) return value
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

function toEpochMs(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }
  if (value instanceof Date) {
    const ms = value.getTime()
    return Number.isFinite(ms) ? ms : undefined
  }
  if (typeof value !== 'string' || value.trim() === '') return undefined

  const asNumber = Number(value)
  if (Number.isFinite(asNumber)) return asNumber

  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

function hasInventoryArrivalWindow(start: unknown, end: unknown): boolean {
  const parsedStart = toEpochMs(start)
  const parsedEnd = toEpochMs(end)
  return parsedStart != null && parsedEnd != null && parsedEnd >= parsedStart
}

// TimePicker labels are UTC clock times. With an inventory window those stamps
// already sit on the booking date (and may cross midnight). With no window the
// picker falls back to getUTCFromNow(), so the same conversion would keep today.
// Apply that clock to booking.date, matching the old HH:mm form path.
function arrivalClockOnBookingDateMs(
  arrivalTime: number,
  bookingDate: number,
  timezone: string
): number {
  const clock = new Date(arrivalTime)
  const hours = `${clock.getUTCHours()}`.padStart(2, '0')
  const minutes = `${clock.getUTCMinutes()}`.padStart(2, '0')
  return new Date(
    formDateTimeToUTCISO(
      new Date(bookingDate).toISOString(),
      `${hours}:${minutes}`,
      timezone
    )
  ).getTime()
}

export function CheckoutContext({
  children,
  venue,
  event,
  affiliateId,
  attributionId,
  initialValues,
  embeddedWidget = false,
}: {
  children: ReactNode
  venue: Venue
  event?: VenueEvent | null
  affiliateId?: string | null
  attributionId?: string | null
  initialValues?: Partial<ICheckoutContext>
  embeddedWidget?: boolean
}) {
  /* related contexts and env states */
  const router = useRouter()
  const session = useSession()
  const inventoryModal = useOpenState()
  const checkoutModal = useOpenState()
  const { theme, iframe } = useQueryParams()

  /* Changeable state management */
  const [bookingId, setBookingId] = useState<string | undefined | null>(
    initialValues?.bookingId
  )
  const [selectedInventory, setSelectedInventory] = useState<InventoryItem>()
  const [fallbackBooking, setFallbackBooking] = useState<Booking | undefined>(
    initialValues?.booking
  )
  const dateDisplayFormat = getLuxonDateFormatByVenueDateDisplayFormat(
    venue.dateDisplayFormat
  )
  const [quantity, setQuantity] = useState<number>(
    fallbackBooking?.quantity ?? 1
  )

  /* Data Hooks for the booking, tickets, and the inventory item */

  const {
    data: booking,
    loading: bookingLoading,
    mutate: mutateBooking,
  } = useBooking(bookingId, {
    config: { fallbackData: fallbackBooking },
  })
  const { data: tickets, loading: ticketsLoading } = useBookingTickets(
    bookingId,
    {
      pause: !booking?.hasTickets,
      config: { fallbackData: initialValues?.tickets },
    }
  )
  const { data: inventory } = useInventory(
    venue ? getId(venue) : null,
    booking?.inventory ?? null,
    { config: { fallbackData: initialValues?.inventory } }
  )

  /* Callbacks and actions to update the state and hook data */

  const setBooking = useCallback(
    (val: Booking) => {
      mutateBooking(val)
      setFallbackBooking(val)
      setBookingId(val.id)
    },
    [mutateBooking]
  )

  const selectInventory = useCallback(
    (inv: InventoryItem) => {
      setSelectedInventory(inv)
      inventoryModal.open()

      analytics.trackEvent({
        event: TrackEvent.ViewedItem,
        venue,
        eventData: {
          date: utcToISO(inv.date),
          dateFormatted: withDateFormat(inv.date, dateDisplayFormat, 'UTC'),
          arrivalStartTime: inv.start != null
            ? withDateFormat(inv.start, 'h:mm a', 'UTC')
            : undefined,
          arrivalEndTime: inv.end != null
            ? withDateFormat(inv.end, 'h:mm a', 'UTC')
            : undefined,
          depositAmount: inv.depositAmount,
          type: inv.type,
          inventoryId: inv.id,
          items: [
            {
              itemName: inv.name,
              itemId: inv.id,
              itemPrice: inv.price,
              itemCategory: inv.type,
              itemQuantity: 1,
            },
          ],
          currency: venue.currencyCode,
        },
      })
      void trackEvent({
        event: TrackingEvent.ViewedItem,
        venueId: venue.id,
        eventId: event?.id ?? undefined,
        inventoryId: inv.id,
      })
    },
    [dateDisplayFormat, event?.id, inventoryModal, venue]
  )

  // Redirect identity has to match the token the API client already sent with
  // createBooking. On a first visit the session prospect token is still empty
  // while Api.getProspectToken() has minted and stored one for the headers.
  const resolveIdentity = useCallback((): CheckoutIdentity => {
    if (session.authToken) return { authToken: session.authToken }
    return { prospectToken: Api.getProspectToken() ?? session.prospectToken }
  }, [session.authToken, session.prospectToken])

  const getOrderPath = useCallback(
    (
      bookingId: string,
      options?: { complete?: boolean } & CheckoutIdentity
    ) => {
      const query: Record<string, string> = {}
      if (iframe) query.iframe = iframe
      if (theme) query.theme = theme

      const identity = resolveCheckoutIdentity({
        authToken: options?.authToken ?? session.authToken,
        sessionProspectToken: session.prospectToken,
        prospectTokenOverride: options?.prospectToken,
      })

      const fullPath = buildCheckoutPath(bookingId, {
        authToken: identity.authToken,
        prospectToken: identity.prospectToken,
        embeddedWidget,
        complete: options?.complete,
      })
      const [pathname, queryString = ''] = fullPath.split('?')
      const identityQuery = Object.fromEntries(
        new URLSearchParams(queryString).entries()
      ) as Record<string, string>

      return { pathname, query: { ...identityQuery, ...query } }
    },
    [embeddedWidget, iframe, session.authToken, session.prospectToken, theme]
  )

  const beginCheckout = useCallback(
    async ({
      inventoryId,
      quantity: qty,
      arrivalTime,
      client,
      eventId,
    }: {
      inventoryId: string
      quantity: number
      arrivalTime?: number | null
      client?: 'web-fast-pass' | 'widget'
      eventId?: string
    }) => {
      // Keep `/i/:id?arrivalTime=<ms>` semantics aligned with tl-web:
      // treat query arrivalTime as a direct epoch timestamp, with no timezone reinterpretation.
      const arrivalTimeTrueUtc = normalizeArrivalTimeMs(arrivalTime)

      const bookingRequest: BookingCreateRequest = {
        affiliateId: affiliateId ?? null,
        attributionId: attributionId ?? null,
        items: [{ inventoryId, quantity: qty }],
        payment: null,
        phone: session.user?.phone
          ? stripPhoneNumber(session.user.phone)
          : undefined,
        pickUpType: null,
        status: 'OPEN',
        trackingId: null,
        type: 'USER',
        arrivalTime: arrivalTimeTrueUtc,
        client,
        eventId,
      }
      const response = await bookingApi.createBooking(bookingRequest)

      setBooking(response)
      setQuantity(response.quantity)
      void trackEvent({
        event: TrackingEvent.AddedItem,
        venueId: venue.id,
        eventId: eventId ?? event?.id ?? undefined,
        inventoryId,
        orderId: response.id,
      })

      const identity = resolveIdentity()
      await router.push(getOrderPath(response.id, identity))

      analytics.trackEvent({
        event: TrackEvent.AddedItem,
        venue: venue,
        eventData: {
          date: utcToISO(response.date),
          dateFormatted: withDateFormat(
            response.date,
            dateDisplayFormat,
            venue.timezone
          ),
          arrivalTime:
            arrivalTimeTrueUtc != null
              ? withDateFormat(arrivalTimeTrueUtc, 'h:mm a', venue.timezone)
              : undefined,
          dueOnArrival: response.dueOnArrival,
          total: response.total,
          type: 'TABLE',
          items: [
            {
              itemName: response.location,
              itemId: inventoryId,
              itemQuantity: qty,
            },
          ],
          currency: venue.currencyCode,
        },
      })

      inventoryModal.close()
    },
    [
      affiliateId,
      attributionId,
      dateDisplayFormat,
      event?.id,
      getOrderPath,
      inventoryModal,
      resolveIdentity,
      router,
      session.user?.phone,
      setBooking,
      venue,
    ]
  )

  const changeQuantity = useCallback(
    async (qty: number | null | undefined) => {
      setQuantity(qty ?? 0)
      const bookingItem = booking?.items[0]
      if (!booking || !bookingItem || !qty) return
      const bookingRequest: BookingCreateRequest = {
        affiliateId: affiliateId ?? booking.affiliate ?? null,
        attributionId: attributionId ?? booking.attributionId ?? null,
        items: [
          {
            inventoryId: bookingItem.inventory.id as string,
            quantity: qty,
          },
        ],
        payment: null,
        phone: session.user?.phone,
        pickUpType: null,
        status: 'OPEN',
        trackingId: null,
        type: 'USER',
      }
      const newBooking = await bookingApi.createBooking(bookingRequest)
      setBooking(newBooking)
      return newBooking
    },
    [affiliateId, attributionId, booking, session.user, setBooking]
  )

  const [checkout, checkingOut] = useRequest(
    async ({
      arrivalTime,
      firstName,
      lastName,
      email,
      phone,
      birthday,
      marketingOptIn,
      userNotes,
    }: CheckoutFormValues) => {
      if (!venue) throw new Error('Venue not found')
      if (!booking) throw new Error('Booking not found')
      if (!inventory) throw new Error('Inventory not found')
      if ((booking.quantity ?? 0) < 1)
        throw new Error('Cannot checkout with less than 1 item')
      const payment = booking.payment

      const profileUpdates = {
        firstName,
        lastName,
        email,
        phone,
        birthday: new Date(birthday).valueOf(),
        marketingOptIn,
      }
      const profileResponse = await session.updateProfile(profileUpdates)

      const venueTimezone = venue?.timezone || venue?.location?.timezone
      // beginCheckout (fast-pass/widget) already stores true UTC.
      // For all existing bookings, arrivalTime is already true UTC from create/updateById.
      // floatingUtcWallClockToTrueUtcMs on that value double-shifts (e.g. 8pm CT -> 1am).
      // Payment links keep the booking time; the checkout dropdown is display-only.
      const paymentLinkCheckout = isPaymentLinkCheckout({
        confirmationRequest:
          inventory.confirmationRequest ?? booking.confirmationRequest,
        isInternal: inventory.isInternal,
      })
      const formArrivalMs = paymentLinkCheckout
        ? undefined
        : normalizeArrivalTimeMs(arrivalTime)
      const arrivalTimeTrueUtcRaw =
        booking.arrivalTime != null
          ? new Date(booking.arrivalTime).valueOf()
          : formArrivalMs != null && venueTimezone
            ? hasInventoryArrivalWindow(
                inventory.start,
                inventory.end
              )
              ? formArrivalMs
              : arrivalClockOnBookingDateMs(
                  formArrivalMs,
                  booking.date,
                  venueTimezone
                )
            : formArrivalMs
      const arrivalTimeTrueUtc =
        normalizeArrivalTimeMs(arrivalTimeTrueUtcRaw) ?? undefined

      const bookingUpdates = {
        arrivalTime: arrivalTimeTrueUtc,
        customerMarketingOptIn: marketingOptIn,
        userId:
          session.loggedIn && !booking.user ? profileResponse?.id : undefined,
        userNotes: userNotes ? `CUSTOMER NOTES: ${userNotes}` : undefined,
      }

      // update booking and profile
      const updatedBooking = await bookingApi
        .update(booking.id, bookingUpdates)
        .catch(async (err) => {
          // There's a case where this booking link was shared and the current user does not "own" this booking.
          // In this case we duplicate the booking so that it will be owned by the current user.
          return bookingApi
            .duplicateBooking(booking, session?.user, bookingUpdates)
            .catch(() => {
              throw err
            })
        })

      // send tracking data
      analytics.trackCheckout({
        ...profileUpdates,
        ...bookingUpdates,
        value: (booking.total ?? 0).toString(),
        currency: venue.currencyCode,
        birthday,
        bookingId: updatedBooking.id,
        venueId: venue.id,
        eventId: event?.id,
      })

      analytics.trackEvent({
        event: TrackEvent.StartedCheckout,
        venue,
        eventData: {
          items: [
            {
              itemName: booking.location,
              itemId: booking.id,
              itemPrice: booking.price,
              itemCategory: booking.inventoryType,
              itemQuantity: 1,
            },
          ],
          currency: venue.currencyCode,
          total: booking.total,
          tax: booking.tax,
          purchaseId: booking.id,
          // promoCode: booking.promoCode,
        },
      })

      setFallbackBooking(updatedBooking)
      clearCart()

      const identity = resolveIdentity()
      const completeRoute = getOrderPath(updatedBooking.id, {
        ...identity,
        complete: true,
      })
      const completeQueryString = new URLSearchParams(completeRoute.query)
      completeQueryString.set('checkoutSessionId', '{CHECKOUT_SESSION_ID}')
      const cancelRoute = getOrderPath(updatedBooking.id, identity)
      const cancelQueryString = new URLSearchParams(cancelRoute.query)

      if (payment === 'NONE') {
        // no payment required, just complete the order
        await bookingApi.complete(updatedBooking.id)
        router.push(completeRoute)
      } else {
        // create checkout session, go to stripe to process payment
        const checkoutResponse = await bookingApi.beginCheckoutSession(
          updatedBooking.id,
          {
            cancelUrl: `${env.domain}${cancelRoute.pathname}${cancelQueryString.toString() ? `?${cancelQueryString.toString()}` : ''}`,
            successUrl: `${env.domain}${completeRoute.pathname}${completeQueryString.toString() ? `?${completeQueryString.toString()}` : ''}`,
          }
        )
        if (embeddedWidget) {
          widgetEventManager.emitStripeCheckoutRedirect(
            iframe,
            checkoutResponse.url
          )
        } else {
          window.location.href = checkoutResponse.url
        }
      }
    },
    [
      booking,
      embeddedWidget,
      event?.id,
      getOrderPath,
      iframe,
      inventory,
      resolveIdentity,
      router,
      session,
      venue,
    ]
  )

  const contextValue = useMemo<ICheckoutContext>(
    () => ({
      embeddedWidget,
      selectedInventory,
      quantity,
      bookingId,
      setBooking,
      booking,
      bookingLoading,
      inventory,
      tickets,
      ticketsLoading,
      selectInventory,
      changeQuantity,
      beginCheckout,
      checkout,
      checkingOut,
      inventoryModal,
      checkoutModal,
    }),
    [
      embeddedWidget,
      selectedInventory,
      quantity,
      bookingId,
      setBooking,
      booking,
      bookingLoading,
      inventory,
      tickets,
      ticketsLoading,
      selectInventory,
      changeQuantity,
      beginCheckout,
      checkout,
      checkingOut,
      inventoryModal,
      checkoutModal,
    ]
  )

  return (
    <checkoutContext.Provider value={contextValue}>
      {children}
    </checkoutContext.Provider>
  )
}
